export * from './components';
export * from './types';

import { ComponentId } from '@omegagrid/core';
import { AppComponent, Application, Container, parseComponentId } from '@reporting/rp-app-host';
import { componentFactories } from './factories';
import { msg } from '@omegagrid/localize';
import { ChatEvent, Sidebar } from './components';
import { ChatDefault } from './types';
import { ChatPoller, ChatUpdate } from './chatPoller';

export class AIApplication extends Application {

	readonly poller = new ChatPoller();

	// Null when the AI API isn't configured.
	private _default?: Promise<ChatDefault | null>;
	async getDefault(): Promise<ChatDefault | null> {
		if (!this._default) {
			this._default = this.container.ms('ai').get<ChatDefault | null>('default');
		}
		return this._default;
	}

	/** Forget the cached default so the next new chat re-reads it (e.g. after the toggles were saved). */
	resetDefault() {
		this._default = undefined;
	}

	async isAvailable(): Promise<boolean> {
		return !!(await this.getDefault());
	}

	async init(container: Container) {
		super.init(container);
		container.services.register('ai');

		this.poller.addEventListener('done', ((e: CustomEvent<ChatUpdate>) => this._onChatDone(e.detail.idChat)) as EventListener);
		this.host.addEventListener('chat.post', async (e: ChatEvent) => {
			if (e.isNew && e.idChat) {
				this.host.updateComponent('ai-chat', {id: `ai-chat-${e.idChat}`, title: `${msg('Chat')} #${e.idChat}`});

				if (this.host.bucket.sidebar1ComponentId == 'ai') {
					((await this.host.getComponent('ai')) as Sidebar).browser?.refresh();
				}
			}
		});
	}

	// Notify only when the chat isn't actually on screen.
	private _onChatDone(idChat: number) {
		const comp = this.host.getComponent(`ai-chat-${idChat}`) as unknown as HTMLElement | undefined;
		const onScreen = !!comp && (comp.checkVisibility?.() ?? comp.offsetParent !== null);
		if (!onScreen) {
			this.host.showAlert({
				title: 'AI',
				content: msg('New reply in chat #{id}', { data: { id: idChat } }),
				type: 'info',
			});
		}
	}

	async newChat() {
		this.host.open('ai-chat', {title: msg('New Chat'), preventDisconnect: true});
	}

	async createComponent(id: ComponentId): Promise<AppComponent> {
		const parsedId = parseComponentId(id);
		const factory = componentFactories.get(parsedId.id as string || parsedId.type);
		if (!factory) return null;
		const component = await factory(this, parsedId);
		return component;
	}

}
